// Metro-area normalization for the Nearby feed.
//
// Users type free-text cities in Settings → Home base. To make "Brooklyn"
// and "Bushwick" find each other (both are NYC) we collapse common
// neighborhoods, boroughs, and sub-cities to a canonical metro key. The
// Nearby matcher compares on the metro key, not the raw string, so users
// who type any name within the same metro see each other's posts.
//
// Anything not in the table falls back to the raw-lowercased first segment
// of the input, so exact city matches still work (e.g. "Tulsa" ↔ "Tulsa")
// without us needing to enumerate every US city.

const METRO_ALIASES: Record<string, string> = {
  // ─── New York City metro ───────────────────────────────────────────
  "new york":            "new york",
  "new york city":       "new york",
  "nyc":                 "new york",
  "ny":                  "new york",
  "manhattan":           "new york",
  "brooklyn":            "new york",
  "queens":              "new york",
  "bronx":               "new york",
  "the bronx":           "new york",
  "staten island":       "new york",
  "bushwick":            "new york",
  "williamsburg":        "new york",
  "greenpoint":          "new york",
  "bed-stuy":            "new york",
  "bedford-stuyvesant":  "new york",
  "bed stuy":            "new york",
  "park slope":          "new york",
  "fort greene":         "new york",
  "crown heights":       "new york",
  "prospect heights":    "new york",
  "harlem":              "new york",
  "east village":        "new york",
  "west village":        "new york",
  "lower east side":     "new york",
  "les":                 "new york",
  "soho":                "new york",
  "tribeca":             "new york",
  "noho":                "new york",
  "chelsea":             "new york",
  "midtown":             "new york",
  "upper east side":     "new york",
  "upper west side":     "new york",
  "ues":                 "new york",
  "uws":                 "new york",
  "long island city":    "new york",
  "lic":                 "new york",
  "astoria":             "new york",
  "ridgewood":           "new york",
  "flushing":            "new york",
  "sunset park":         "new york",
  "ditmas park":         "new york",
  "bay ridge":           "new york",
  "jersey city":         "new york",
  "hoboken":             "new york",
  "newark":              "new york",

  // ─── Los Angeles metro ─────────────────────────────────────────────
  "los angeles":         "los angeles",
  "la":                  "los angeles",
  "l.a.":                "los angeles",
  "hollywood":           "los angeles",
  "west hollywood":      "los angeles",
  "weho":                "los angeles",
  "santa monica":        "los angeles",
  "venice":              "los angeles",
  "silverlake":          "los angeles",
  "silver lake":         "los angeles",
  "echo park":           "los angeles",
  "los feliz":           "los angeles",
  "highland park":       "los angeles",
  "downtown la":         "los angeles",
  "dtla":                "los angeles",
  "long beach":          "los angeles",
  "pasadena":            "los angeles",
  "burbank":             "los angeles",
  "glendale":            "los angeles",
  "culver city":         "los angeles",
  "santa clarita":       "los angeles",
  "studio city":         "los angeles",
  "north hollywood":     "los angeles",
  "noho la":             "los angeles",
  "eagle rock":          "los angeles",
  "atwater village":     "los angeles",
  "los angeles county":  "los angeles",

  // ─── San Francisco Bay Area ────────────────────────────────────────
  "san francisco":       "san francisco",
  "sf":                  "san francisco",
  "oakland":             "san francisco",
  "berkeley":            "san francisco",
  "san jose":            "san francisco",
  "palo alto":           "san francisco",
  "mountain view":       "san francisco",
  "the mission":         "san francisco",
  "mission district":    "san francisco",
  "soma":                "san francisco",
  "bay area":            "san francisco",

  // ─── Chicago metro ─────────────────────────────────────────────────
  "chicago":             "chicago",
  "chi":                 "chicago",
  "wicker park":         "chicago",
  "logan square":        "chicago",
  "lincoln park":        "chicago",
  "humboldt park":       "chicago",
  "pilsen":              "chicago",
  "evanston":            "chicago",
  "oak park":            "chicago",

  // ─── Philadelphia metro ────────────────────────────────────────────
  "philadelphia":        "philadelphia",
  "philly":              "philadelphia",
  "fishtown":            "philadelphia",
  "south philly":        "philadelphia",
  "northern liberties":  "philadelphia",

  // ─── Boston metro ──────────────────────────────────────────────────
  "boston":              "boston",
  "cambridge":           "boston",
  "somerville":          "boston",
  "brookline":           "boston",
  "allston":             "boston",
  "jamaica plain":       "boston",

  // ─── Washington DC metro ───────────────────────────────────────────
  "washington dc":       "washington dc",
  "washington d.c.":     "washington dc",
  "washington, dc":      "washington dc",
  "washington":          "washington dc",
  "dc":                  "washington dc",
  "d.c.":                "washington dc",
  "arlington":           "washington dc",
  "alexandria":          "washington dc",
  "silver spring":       "washington dc",
  "bethesda":            "washington dc",

  // ─── Atlanta ───────────────────────────────────────────────────────
  "atlanta":             "atlanta",
  "atl":                 "atlanta",
  "decatur":             "atlanta",

  // ─── Miami ─────────────────────────────────────────────────────────
  "miami":               "miami",
  "miami beach":         "miami",
  "south beach":         "miami",
  "wynwood":             "miami",
  "little havana":       "miami",
  "coral gables":        "miami",

  // ─── Austin ────────────────────────────────────────────────────────
  "austin":              "austin",
  "atx":                 "austin",
  "east austin":         "austin",

  // ─── Portland OR ───────────────────────────────────────────────────
  "portland":            "portland",
  "pdx":                 "portland",

  // ─── Seattle ───────────────────────────────────────────────────────
  "seattle":             "seattle",
  "capitol hill":        "seattle",
  "ballard":             "seattle",

  // ─── Denver ────────────────────────────────────────────────────────
  "denver":              "denver",
  "rino":                "denver",

  // ─── Nashville ─────────────────────────────────────────────────────
  "nashville":           "nashville",
  "east nashville":      "nashville",

  // ─── New Orleans ───────────────────────────────────────────────────
  "new orleans":         "new orleans",
  "nola":                "new orleans",
  "marigny":             "new orleans",
  "bywater":             "new orleans",

  // ─── Detroit ───────────────────────────────────────────────────────
  "detroit":             "detroit",
  "ferndale":            "detroit",
  "hamtramck":           "detroit",

  // ─── Minneapolis / St Paul ─────────────────────────────────────────
  "minneapolis":         "minneapolis",
  "st paul":             "minneapolis",
  "saint paul":          "minneapolis",
  "twin cities":         "minneapolis",

  // ─── International ─────────────────────────────────────────────────
  "london":              "london",
  "paris":               "paris",
  "tokyo":               "tokyo",
  "berlin":              "berlin",
  "amsterdam":           "amsterdam",
  "toronto":             "toronto",
  "montreal":            "montreal",
  "vancouver":           "vancouver",
  "mexico city":         "mexico city",
  "cdmx":                "mexico city",
};

// Normalize a free-text city to its canonical metro key.
// - Lowercases and trims.
// - Drops trailing state / country suffixes ("Brooklyn, NY" → "brooklyn",
//   "London, UK" → "london") by taking the first comma-separated segment.
// - Maps the result through METRO_ALIASES; falls back to the literal first
//   segment when no alias matches so exact-city matching still works for
//   smaller cities not enumerated above.
export function normalizeToMetro(input: string | null | undefined): string {
  if (!input) return "";
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return "";
  const firstSegment = trimmed.split(",")[0].trim();
  if (!firstSegment) return "";
  return METRO_ALIASES[firstSegment] ?? firstSegment;
}
